/**
 * Option B service-tile → destination map (M12-T01, ADR-0026).
 *
 * The home shows these six tiles instead of the chip system; each routes to its
 * workflow with the mapped chip pre-applied via the URL. Kept next to `chips.ts`
 * so the tile→chip mapping is one documented constant (ADR-0026 mitigation).
 * Chip vocabulary/logic is NOT redefined here. Emergency opens Emergency Help
 * (ADR-0064) at `/emergency` (the M15 call-ahead flow was retired at M36-T11).
 *
 * The sixth tile is Transport (M34-T04, ADR-0063): Medcor Transport is a
 * scheduled, NON-emergency service, so the tile is accent blue, not SOS red,
 * and uses a car rather than the truck. It replaced "Ambulance" (M19-T04 sent
 * that to a static "AMTC arranges ambulances" page). `/ambulance` and
 * `/?row1=ambulance` now redirect to `/transport`; the `row1=ambulance` API
 * filter is unchanged.
 */
import type { IconName } from './Icon'

export interface Tile {
  label: string
  icon: IconName
  /** Router destination. `/?row1=<chip>` opens the results screen (§2.2). */
  to: string
  /** Emergency tints the icon SOS-red; everything else accent blue. */
  sos?: boolean
  /**
   * Image for the tile's well, keyed as in the emitted manifest. Absent means
   * the well keeps its icon. Five carry one; Transport shows its car icon until
   * the owner supplies a non-ambulance photograph (spec Q16).
   *
   * T18c left the two `sos` tiles on their glyphs, reasoning that §2.2 puts the
   * SOS-red tint on the icon and a photograph cannot carry a tint. The owner
   * supplied images for both, and the objection does not survive them: each is
   * itself dominated by red — the ED's sign and crosses, the ambulance's
   * stripes and light bar — so the red signal moves into the picture rather
   * than being lost. `sos` still governs the label and the fallback glyph.
   */
  image?: string
}

export const TILES: readonly Tile[] = [
  { label: 'Beds', icon: 'bed', to: '/beds', image: 'services/bed' },
  {
    label: 'Emergency',
    icon: 'sos',
    to: '/emergency',
    sos: true,
    image: 'services/emergency',
  },
  {
    label: 'Medicines',
    icon: 'pill',
    to: '/medicines',
    image: 'services/medicine',
  },
  {
    label: 'Labs, scans & procedures',
    icon: 'flask',
    to: '/diagnostics',
    image: 'services/labtest',
  },
  {
    label: 'Specialists',
    icon: 'user',
    to: '/specialists',
    image: 'services/specialist',
  },
  { label: 'Transport', icon: 'car', to: '/transport' },
] as const
